import type { BooleanFlag, MediaType } from './baseTypes';
import type { CustomFeature } from './featureTypes';

export interface Hotspot {
  id: string;
  zOrder: number;
  icon: Partial<HSIcon>;
  title: string;
  showTitle: BooleanFlag;
  titleSetting: HSTitleSetting;
  // groupId: number;
  data: CustomFeature;
  visible: BooleanFlag;
  locked: BooleanFlag;
  /** 第三方ID, v5.8.1新增 */
  partnerId?: string;
  /** 全景视频热点显示设置, v5.8.3 */
  display?: HSDisplaySetting;
  /** 热点高级交互设置, v5.9.0 */
  extendAction?: HSExtendAction;
}

export type HSIcon =
  | HSIconSystem
  | HSIconMark
  | HSIconImage
  | HSIconSeqFrame
  | HSIconVideo
  | HSIconPolygon;

export type HSNonPolygonIcon =
  | HSIconSystem
  | HSIconMark
  | HSIconImage
  | HSIconSeqFrame
  | HSIconVideo;

export enum HSIconType {
  system = 0,
  mark = 1,
  image = 2,
  seqFrame = 3,
  video = 4,
  polygon = 5,
}

export type HSEdge =
  | 'topleft'
  | 'top'
  | 'topright'
  | 'left'
  | 'center'
  | 'right'
  | 'leftbottom'
  | 'bottom'
  | 'rightbottom';

export type HSPos = 'top' | 'left' | 'right' | 'bottom';

export type HSAlign = 'left' | 'center' | 'right' | 'justify';

export enum HSIconAutoPlay {
  click = 0,
  auto = 1,
}

export interface HSIconStickSetting {
  /** 是否贴合全景，跟随画面同步缩放, 1开启, 0关闭, 默认0 (v5.3.4) */
  stick?: BooleanFlag;
  /**
   * 固定水平垂直显示, 默认false (v5.8.1)
   * 数据在krpano中与编辑器的交互逻辑不一致
   * 编辑器中需要打开stick才能设置zoom
   * 在krpano中zoom为true时需要stick为0才能生效
   */
  zoom?: boolean;
}

export type HSIconSystem = {
  type: HSIconType.system;
  ath: number;
  atv: number;
  scaleX: number;
  scaleY: number;
  /** 锚点，默认 center */
  edge: HSEdge;
  /** 地址 */
  url: string;
  /** 帧数 */
  frame: number;
} & HSIconStickSetting;

export type HSIconMark = {
  // 文字标记
  type: HSIconType.mark;
  // 水平坐标
  ath: number;
  // 垂直坐标
  atv: number;
  // 相对图标位置, 'top'|'left'|'right'|'bottom';
  edge: HSPos;
  // 标记线长度, px
  length: number;
  // 标记样式, 默认0, 0直线｜1三角形｜2圆形端点线｜3圆环端点线 (v5.3.4)
  lineType?: 0 | 1 | 2 | 3;
  // 标记线宽，默认1，1-10px (v5.3.4)
  lineWidth?: number;
  // 标记线颜色, 标记颜色, 默认"255,255,255,1" (v5.3.4)
  lineColor?: string;
  media?: {
    enabled: BooleanFlag;
    type?: MediaType;
    id?: number;
    url?: string;
  };
} & HSIconStickSetting;

export enum HSPosType {
  normal = 0,
  adv = 1,
}

export type HSIconCommom = {
  scaleLock: BooleanFlag; // 锁定宽高比, 1开启, 0关闭, 默认1
  scaleX: number; // 宽度缩放
  scaleY: number; // 高度缩放
  positionType: HSPosType; // 对齐方式，0手动调整，1参数对齐，默认0
  rx: number; // x轴旋转, stick = 1 时可用
  ry: number; // y轴旋转, stick = 1 时可用
  rz: number; // z轴旋转, stick = 1 时可用
  hFov?: number; // 图标对齐 - 水平视场
  yaw?: number; // 图标对齐 - x轴旋转
  pitch?: number; // 图标对齐 - y轴旋转
  roll?: number; // 图标对齐 - z轴旋转
  chromakey?: string; // 透明蒙板 '0x238E54,0.3,0.1'
} & HSIconStickSetting;

export type HSIconImageMedia = {
  id: number;
  url: string;
};

export type HSIconImage = {
  type: HSIconType.image; // 图片
  ath: number; // 水平坐标
  atv: number; // 垂直坐标
  edge: HSEdge; // // 锚点，默认 center
  media: HSIconImageMedia[];
  autoPlayType: HSIconAutoPlay; // 0手动播放，1自动播放，（多张图时可用）
  autoPlayDuration: number; // 自动播放时间隔，秒
} & HSIconCommom;

export type HSIconSeqFrame = {
  type: HSIconType.seqFrame; // 序列帧
  ath: number; // 水平坐标
  atv: number; // 垂直坐标
  edge: HSEdge; // // 锚点，默认 center
  media:
    | [
        {
          id: number;
          url: string;
          width: number; // 宽，px
          height: number; // 高，px
          frame: number; // 帧数
          duration: number; // 帧间隔，秒
        },
      ]
    | [];
} & HSIconCommom;

export type HSIconVideo = {
  type: HSIconType.video; // 序列帧
  ath: number; // 水平坐标
  atv: number; // 垂直坐标
  edge: HSEdge; // // 锚点，默认 center
  media:
    | [
        {
          id: number;
          url: string;
          name: string;
          coverUrl: string;
        },
      ]
    | [];
  autoPlay: BooleanFlag; // 自动播放, 0:不关闭 1:关闭 默认1
  loop: BooleanFlag; // 循环播放, 0:不关闭 1:关闭 默认1
  mute: BooleanFlag; // 静音播放, 0:不关闭 1:关闭 默认0
  turnOffBgMusicOnPlay: BooleanFlag; // 播放时关闭背景音乐, 0:不关闭 1:关闭 默认0
  turnOffBgVoiceOnPlay: BooleanFlag; // 播放时关闭背景讲解, 0:不关闭 1:关闭 默认0
} & HSIconCommom;

export interface HSIconPolygon {
  type: HSIconType.polygon; // 多边形
  points: { ath: number; atv: number }[]; // 多边形点数组, [{ath: 0, atv: 10}, {ath: 10, atv: 0}, {ath: 0, atv: 0}]
  ath?: number; // 标题水平坐标
  atv?: number; // 标题垂直坐标
  fillColor: string; // 热点填充颜色 RGBA
  borderWidth: number; // 边框宽度
  borderColor: string; // 边框颜色 '0,0,0,1' RGBA
  overFillColor: string; // 鼠标移入热点填充颜色 RGBA
  overBorderWidth: number; // 鼠标移入边框宽度
  overBorderColor: string; // 鼠标移入边框颜色 RGBA
  blink?: BooleanFlag; // 移入和默认状态交替变化
}

export interface HSTitleSetting {
  /** 文本对齐方式 */
  align: HSAlign;
  /** 相对图标位置 top, right, bottom, left */
  edge: HSPos;
  /** x轴偏移 */
  ox: number;
  /** y轴偏移 */
  oy: number;
  font: {
    fontSize: number;
    fontColor: string;
  };
  background: {
    /** 背景色类型，0纯色，1渐变色，默认0 */
    type?: 0 | 1;
    /** 背景色 '0,0,0,1' RGBA */
    bgColor: string;
    /** 背景圆角 */
    bgRoundedge: number;
    /** 背景描边宽度 */
    borderWidth: number;
    /** 背景描边颜色 '0,0,0,1' RGBA */
    borderColor: string;
    /** 渐变角度 */
    gradientAngle?: number;
    /** 渐变起始颜色 */
    gradientStartColor?: string;
    /** 渐变结束颜色 */
    gradientEndColor?: string;
  };
  /** 样式类型，0自定义，1特殊，默认0 */
  styleType?: 0 | 1;
  /** 特殊样式id */
  specialStyleId?: number;
  /** 文字方向, 0 横排，1 竖排（从左到右），默认0 */
  writingMode?: 0 | 1;
}

export interface HSDisplaySetting {
  /** 显示模式，0始终显示，1自定义显示时段, 默认: 0 */
  mode: 0 | 1;
  /** 开始显示时间，单位为秒 */
  startAt: number;
  /** 结束显示时间，单位为秒 */
  endAt: number;
}

export interface HSExtendActionItem {
  /** 图层/热点 id */
  id: string;
  /** 0隐藏 1显示 2切换 */
  action: 0 | 1 | 2;
  /** 时长 */
  duration: number;
}

export interface HSExtendAction {
  hotspots?: HSExtendActionItem[];
  layers?: HSExtendActionItem[];
}
